import { InputMaybe, PreferredHotelProviderType, PriceComparisonPayload, PriceComparisonType } from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useMemo } from 'react';

const isValidPriceComparisonType = (value?: InputMaybe<PriceComparisonType>): value is PriceComparisonType =>
  Object.values(PriceComparisonType).includes(value as PriceComparisonType);

const isValidPreferredHotelProviderType = (value?: InputMaybe<PreferredHotelProviderType>): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

const isValidMarginPercentage = (value?: InputMaybe<number>): value is number => typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;

const isValidPriceComparePayload = (payload: PriceComparisonPayload | undefined) => {
  if (!payload) return false;
  return (
    isValidPriceComparisonType(payload.comparisonType) &&
    isValidPreferredHotelProviderType(payload.preferredProvider) &&
    isValidMarginPercentage(payload.preferredProviderPriceMaxExtraPercent)
  );
};

/**
 * Intended to be used to generate the metadata argument in the
 * `travelPlacesPricingAndBookingDetails` and `searchExtendedAccommodationProperties`
 * queries.
 */
export const useSearchAccommodationsQueryMetadataPayload = (): {
  isPreparingSearchMetadataPayload: boolean;
  searchMetadata: {
    eventId: string | undefined;
    preferredProviderType: PreferredHotelProviderType | undefined;
    priceComparisonPayload: PriceComparisonPayload | undefined;
  } | null;
} => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();

  const {
    value: accommodationsGuestPriceCompareType,
    loading: accommodationsGuestPriceCompareTypeLoading,
    payload: accommodationsGuestPriceCompareTypeLoadingPayload
  } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');

  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;
  const priceComparisonPayload = useMemo(() => {
    const remotePayload = (Object(accommodationsGuestPriceCompareTypeLoadingPayload) as PriceComparisonPayload) || {};
    return {
      comparisonType: remotePayload.comparisonType,
      preferredProvider: remotePayload.preferredProvider,
      preferredProviderPriceMaxExtraPercent: remotePayload.preferredProviderPriceMaxExtraPercent
    };
  }, [accommodationsGuestPriceCompareTypeLoadingPayload]);

  const shouldUsePriceComparison = accommodationsGuestPriceCompareType === 'pricecomparison';
  let validPriceComparisonPayload: PriceComparisonPayload | undefined;
  if (shouldUsePriceComparison && isValidPriceComparePayload(priceComparisonPayload)) {
    validPriceComparisonPayload = priceComparisonPayload;
  }
  // Validate preferredProviderType values come from amplitude
  let validPreferredProviderType: PreferredHotelProviderType | undefined;
  if ((!shouldUsePriceComparison || !validPriceComparisonPayload) && isValidPreferredHotelProviderType(preferredProviderType)) {
    validPreferredProviderType = preferredProviderType;
  }

  const isPreparingSearchMetadataPayload = loadingEventInfo || accommodationsGuestPriceCompareTypeLoading;

  return useMemo(() => {
    return {
      isPreparingSearchMetadataPayload,
      searchMetadata: isPreparingSearchMetadataPayload
        ? null
        : {
            eventId: eventInfo?.eventId,
            priceComparisonPayload: validPriceComparisonPayload,
            preferredProviderType: validPreferredProviderType
          }
    };
  }, [isPreparingSearchMetadataPayload, eventInfo?.eventId, validPriceComparisonPayload, validPreferredProviderType]);
};
